import type {GridPoint, LevelGeometryDocument} from "@/dungeons/model/DungeonGeometry.ts";

export type GeometryPointReference =
    | {type: "wall"; wallId: string; pointIndex: number}
    | {type: "door"; doorId: string; endpoint: "start" | "end"}
    | {type: "transition"; transitionId: string; pointIndex: number};

export type GeometrySnapResult =
    | {target: "free"; point: GridPoint}
    | {target: "point"; point: GridPoint; source: GeometryPointReference}
    | {target: "segment"; point: GridPoint; wallId: string; segmentIndex: number}
    | {target: "grid-intersection"; point: GridPoint}
    | {target: "grid-line"; point: GridPoint; axis: "x" | "y"};

export type GeometrySnapTarget = GeometrySnapResult["target"];

export type ExcludedGeometrySnapTarget =
    | {type: "wall"; wallId: string; pointIndex: number}
    | {type: "door"; doorId: string}
    | {type: "transition"; transitionId: string};

export type GeometrySnapRequest = {
    point: GridPoint;
    document: LevelGeometryDocument;
    pixelsPerCell: number;
    enabled: boolean;
    excluded?: ExcludedGeometrySnapTarget;
};

const POINT_TOLERANCE_PIXELS = 10;
const SEGMENT_TOLERANCE_PIXELS = 10;
const GRID_INTERSECTION_TOLERANCE_PIXELS = 10;
const GRID_LINE_TOLERANCE_PIXELS = 10;
const MAX_GRID_LINE_CELL_FRACTION = 0.25;

const round = (value: number) => Math.round(value * 10_000) / 10_000;
const distanceSquared = (first: GridPoint, second: GridPoint) =>
    (first.x - second.x) ** 2 + (first.y - second.y) ** 2;

const findClosestFeaturePoint = ({point, document, pixelsPerCell, excluded}: GeometrySnapRequest) => {
    const toleranceSquared = (POINT_TOLERANCE_PIXELS / pixelsPerCell) ** 2;
    let closest: Extract<GeometrySnapResult, {target: "point"}> | undefined;
    let closestDistance = Number.POSITIVE_INFINITY;
    const consider = (candidate: GridPoint, source: GeometryPointReference) => {
        const distance = distanceSquared(point, candidate);
        if (distance <= toleranceSquared && distance < closestDistance) {
            closestDistance = distance;
            closest = {point: candidate, target: "point", source};
        }
    };

    document.walls.forEach(wall => wall.points.forEach((candidate, pointIndex) => {
        if (excluded?.type === "wall" && excluded.wallId === wall.id
            && Math.abs(pointIndex - excluded.pointIndex) <= 1) return;
        consider(candidate, {type: "wall", wallId: wall.id, pointIndex});
    }));
    document.doors.forEach(door => {
        if (excluded?.type === "door" && excluded.doorId === door.id) return;
        consider(door.start, {type: "door", doorId: door.id, endpoint: "start"});
        consider(door.end, {type: "door", doorId: door.id, endpoint: "end"});
    });
    document.transitions.forEach(transition => {
        if (excluded?.type === "transition" && excluded.transitionId === transition.id) return;
        transition.points.forEach((candidate, pointIndex) =>
            consider(candidate, {type: "transition", transitionId: transition.id, pointIndex}));
    });
    return closest;
};

const findClosestWallSegment = ({point, document, pixelsPerCell, excluded}: GeometrySnapRequest) => {
    const toleranceSquared = (SEGMENT_TOLERANCE_PIXELS / pixelsPerCell) ** 2;
    let closest: Extract<GeometrySnapResult, {target: "segment"}> | undefined;
    let closestDistance = Number.POSITIVE_INFINITY;

    document.walls.forEach(wall => {
        for (let segmentIndex = 0; segmentIndex < wall.points.length - 1; segmentIndex++) {
            if (excluded?.type === "wall" && excluded.wallId === wall.id
                && (segmentIndex === excluded.pointIndex - 1 || segmentIndex === excluded.pointIndex)) continue;
            const start = wall.points[segmentIndex];
            const end = wall.points[segmentIndex + 1];
            const dx = end.x - start.x;
            const dy = end.y - start.y;
            const lengthSquared = dx * dx + dy * dy;
            if (lengthSquared === 0) continue;
            const factor = Math.max(0, Math.min(1,
                ((point.x - start.x) * dx + (point.y - start.y) * dy) / lengthSquared));
            const projected = {x: round(start.x + factor * dx), y: round(start.y + factor * dy)};
            const distance = distanceSquared(point, projected);
            if (distance <= toleranceSquared && distance < closestDistance) {
                closestDistance = distance;
                closest = {point: projected, target: "segment", wallId: wall.id, segmentIndex};
            }
        }
    });
    return closest;
};

const snapGridIntersection = ({point, pixelsPerCell}: GeometrySnapRequest):
    Extract<GeometrySnapResult, {target: "grid-intersection"}> | undefined => {
    const intersection = {x: Math.round(point.x), y: Math.round(point.y)};
    const toleranceSquared = (GRID_INTERSECTION_TOLERANCE_PIXELS / pixelsPerCell) ** 2;
    return distanceSquared(point, intersection) <= toleranceSquared
        ? {point: intersection, target: "grid-intersection"}
        : undefined;
};

const snapGridLine = ({point, pixelsPerCell}: GeometrySnapRequest): GeometrySnapResult | undefined => {
    const intersection = {x: Math.round(point.x), y: Math.round(point.y)};
    const xDistance = Math.abs(point.x - intersection.x);
    const yDistance = Math.abs(point.y - intersection.y);
    const tolerance = Math.min(GRID_LINE_TOLERANCE_PIXELS / pixelsPerCell, MAX_GRID_LINE_CELL_FRACTION);
    if (xDistance > tolerance && yDistance > tolerance) return undefined;
    return xDistance <= yDistance
        ? {point: {x: intersection.x, y: point.y}, target: "grid-line", axis: "x"}
        : {point: {x: point.x, y: intersection.y}, target: "grid-line", axis: "y"};
};

export const snapGeometryPoint = (request: GeometrySnapRequest): GeometrySnapResult => {
    if (!request.enabled) return {point: request.point, target: "free"};
    const featurePoint = findClosestFeaturePoint(request);
    const gridIntersection = snapGridIntersection(request);
    const closestPoint = featurePoint && gridIntersection
        ? distanceSquared(request.point, gridIntersection.point) < distanceSquared(request.point, featurePoint.point)
            ? gridIntersection : featurePoint
        : featurePoint ?? gridIntersection;
    return closestPoint
        ?? findClosestWallSegment(request)
        ?? snapGridLine(request)
        ?? {point: request.point, target: "free"};
};
